import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { searchLocalities } from "@/api/localities";

/** Debounce of the locality search box (TASK-5176 AC: 250 ms). */
export const LOCALITY_SEARCH_DEBOUNCE_MS = 250;

/** The server answers `[]` below this, so the client does not ask. */
export const LOCALITY_SEARCH_MIN_CHARS = 2;

/**
 * Debounced prefix search over the global locality reference (Sprint 228).
 *
 * Folding (`ё`/`е`, case) is the SERVER's job — the client never filters the
 * results again. `keepPreviousData` keeps the last list on screen between
 * keystrokes so it does not blink empty; the reference data is global and
 * changes quarterly at most, hence the 5-minute stale time.
 */
export function useLocalitySearch(query: string) {
  const trimmed = query.trim();
  const [debounced, setDebounced] = useState(trimmed);

  useEffect(() => {
    const id = setTimeout(
      () => setDebounced(trimmed),
      LOCALITY_SEARCH_DEBOUNCE_MS,
    );
    return () => clearTimeout(id);
  }, [trimmed]);

  const enabled = debounced.length >= LOCALITY_SEARCH_MIN_CHARS;
  const result = useQuery({
    queryKey: ["localities", "search", debounced],
    queryFn: () => searchLocalities(debounced, 20),
    enabled,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });

  return {
    ...result,
    /** The query text the current results answer (after the debounce). */
    debouncedQuery: debounced,
    /** False below the minimum length — the list must stay hidden then. */
    active: enabled && trimmed.length >= LOCALITY_SEARCH_MIN_CHARS,
    /** True while the user is still typing and the debounce has not fired. */
    isDebouncing: trimmed !== debounced,
  };
}
